import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Button, Container, Alert, Spinner, Card } from 'react-bootstrap';
import { getFace, updateFace, addMeeting, updateMeeting, deleteMeeting } from '../api';
import { useAccessToken } from '../hooks/useAccessToken';

function EditFaceForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const getToken = useAccessToken();
  const [formData, setFormData] = useState({
    name: '',
    linkedin_url: '',
    position: '',
    organization: '',
    email: '',
    sector: '',
    how_met: '',
    general_notes: '',
  });
  const [meetings, setMeetings] = useState([]);
  const [newMeetings, setNewMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchFace = async () => {
      try {
        setLoading(true);
        const token = await getToken();
        const data = await getFace(id, token);
        setFormData({
          name: data.name || '',
          linkedin_url: data.linkedin_url || '',
          position: data.position || '',
          organization: data.organization || '',
          email: data.email || '',
          sector: data.sector || '',
          how_met: data.how_met || '',
          general_notes: data.general_notes || '',
        });
        setMeetings(data.meetings || []);
      } catch (err) {
        setError(err.message || 'Failed to fetch face details.');
      } finally {
        setLoading(false);
      }
    };
    fetchFace();
  }, [id, getToken]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleMeetingChange = (index, field, value) => {
    const updatedMeetings = [...meetings];
    updatedMeetings[index] = { ...updatedMeetings[index], [field]: value };
    setMeetings(updatedMeetings);
  };

  const handleNewMeetingChange = (index, field, value) => {
    const updatedNewMeetings = [...newMeetings];
    updatedNewMeetings[index] = { ...updatedNewMeetings[index], [field]: value };
    setNewMeetings(updatedNewMeetings);
  };

  const addNewMeetingField = () => {
    setNewMeetings([...newMeetings, { meeting_date: '', meeting_notes: '' }]);
  };

  const removeNewMeeting = (index) => {
    const updatedNewMeetings = newMeetings.filter((_, i) => i !== index);
    setNewMeetings(updatedNewMeetings);
  };

  const handleDeleteMeeting = async (meetingId, index) => {
    try {
      const token = await getToken();
      await deleteMeeting(meetingId, token);
      const updatedMeetings = meetings.filter((_, i) => i !== index);
      setMeetings(updatedMeetings);
    } catch (err) {
      setError(err.message || 'Failed to delete meeting.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const token = await getToken();

      // Update face data
      await updateFace(id, formData, token);

      // Update existing meetings
      for (const meeting of meetings) {
        if (meeting.id) {
          await updateMeeting(meeting.id, {
            meeting_date: meeting.meeting_date,
            meeting_notes: meeting.meeting_notes,
          }, token);
        }
      }

      // Add new meetings
      for (const newMeeting of newMeetings) {
        if (newMeeting.meeting_date) {
          await addMeeting(id, {
            meeting_date: newMeeting.meeting_date,
            meeting_notes: newMeeting.meeting_notes,
          }, token);
        }
      }

      setSuccess(true);
      setTimeout(() => {
        navigate(`/face/${id}`);
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to update face.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p>Loading...</p>
      </Container>
    );
  }

  return (
    <Container className="my-4">
      <h2>Edit Person</h2>
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">Person updated successfully!</Alert>}
      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Name <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            disabled={saving}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>LinkedIn Profile URL</Form.Label>
          <Form.Control
            type="url"
            name="linkedin_url"
            value={formData.linkedin_url}
            onChange={handleChange}
            disabled={saving}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Position / Title</Form.Label>
          <Form.Control
            type="text"
            name="position"
            value={formData.position}
            onChange={handleChange}
            disabled={saving}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Organization</Form.Label>
          <Form.Control
            type="text"
            name="organization"
            value={formData.organization}
            onChange={handleChange}
            disabled={saving}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            disabled={saving}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Sector</Form.Label>
          <Form.Select
            name="sector"
            value={formData.sector}
            onChange={handleChange}
            disabled={saving}
          >
            <option value="">Select...</option>
            <option value="Congress">Congress</option>
            <option value="Private Sector">Private Sector</option>
            <option value="Lobbyist">Lobbyist</option>
            <option value="Other">Other</option>
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>How I originally met them</Form.Label>
          <Form.Control
            as="textarea"
            name="how_met"
            value={formData.how_met}
            onChange={handleChange}
            rows={3}
            disabled={saving}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>General Notes</Form.Label>
          <Form.Control
            as="textarea"
            name="general_notes"
            value={formData.general_notes}
            onChange={handleChange}
            rows={5}
            disabled={saving}
          />
        </Form.Group>

        <h4 className="mt-4 mb-3">Meetings</h4>

        {meetings.map((meeting, index) => (
          <Card key={meeting.id} className="mb-3 p-3">
            <Form.Group className="mb-2">
              <Form.Label>Date</Form.Label>
              <Form.Control
                type="date"
                value={meeting.meeting_date}
                onChange={(e) => handleMeetingChange(index, 'meeting_date', e.target.value)}
                disabled={saving}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={meeting.meeting_notes || ''}
                onChange={(e) => handleMeetingChange(index, 'meeting_notes', e.target.value)}
                disabled={saving}
              />
            </Form.Group>
            <Button
              variant="danger"
              size="sm"
              onClick={() => handleDeleteMeeting(meeting.id, index)}
              disabled={saving}
            >
              Delete Meeting
            </Button>
          </Card>
        ))}

        {newMeetings.map((meeting, index) => (
          <Card key={`new-${index}`} className="mb-3 p-3 border-success">
            <Form.Group className="mb-2">
              <Form.Label>Date <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="date"
                value={meeting.meeting_date}
                onChange={(e) => handleNewMeetingChange(index, 'meeting_date', e.target.value)}
                disabled={saving}
                required
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={meeting.meeting_notes || ''}
                onChange={(e) => handleNewMeetingChange(index, 'meeting_notes', e.target.value)}
                disabled={saving}
              />
            </Form.Group>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => removeNewMeeting(index)}
              disabled={saving}
            >
              Remove
            </Button>
          </Card>
        ))}

        <Button
          variant="outline-primary"
          onClick={addNewMeetingField}
          className="mb-4"
          disabled={saving}
        >
          + Add Meeting
        </Button>

        <div className="d-flex gap-2">
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button variant="secondary" onClick={() => navigate(`/face/${id}`)} disabled={saving}>
            Cancel
          </Button>
        </div>
      </Form>
    </Container>
  );
}

export default EditFaceForm;
